import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  TagIcon, 
  GlobeAltIcon, 
  EnvelopeIcon,
  UserGroupIcon,
  MapPinIcon,
  StarIcon,
  ComputerDesktopIcon,
  BriefcaseIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { getDiscountById } from '../../lib/db';
import Logo from '../../components/common/Logo';

const DiscountDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [discount, setDiscount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    const fetchDiscount = async () => {
      if (!id) return;
      
      setLoading(true);
      try {
        // First try to get by ID (if it's a UUID format)
        if (id.includes('-') && id.length > 20) {
          const data = await getDiscountById(id);
          setDiscount(data);
        } else {
          // If it's a slug, we need to fetch all discounts and find by slug
          const { getDiscounts } = await import('../../lib/db');
          const allDiscounts = await getDiscounts();
          const foundDiscount = allDiscounts.find(discount => discount.slug === id);
          setDiscount(foundDiscount || null);
        }
      } catch (error) {
        console.error('Error fetching discount:', error);
        setDiscount(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDiscount();
  }, [id]);

  const handleCopyCode = () => {
    if (discount?.discount_code) {
      navigator.clipboard.writeText(discount.discount_code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const addUTMParameters = (url) => {
    if (!url) return url;
    
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('utm_source', 'membership.openorg.fyi');
      urlObj.searchParams.set('utm_medium', 'referral');
      urlObj.searchParams.set('utm_campaign', 'marketplace');
      urlObj.searchParams.set('utm_content', 'discount_detail');
      return urlObj.toString();
    } catch (error) {
      // If URL is invalid, return original
      console.warn('Invalid URL for UTM parameters:', url);
      return url;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 w-48 bg-gray-800 rounded mb-8"></div>
            <div className="h-96 bg-gray-800 rounded-lg mb-8"></div>
            <div className="space-y-4">
              <div className="h-4 w-3/4 bg-gray-800 rounded"></div>
              <div className="h-4 w-1/2 bg-gray-800 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!discount) {
    return (
      <div className="min-h-screen bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Marketplace offer not found</h1>
            <button
              onClick={() => navigate(-1)}
              className="text-primary hover:text-primary-light"
            >
              Go back to marketplace
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to marketplace
        </button>

        {/* Main content */}
        <div className="bg-gray-800 rounded-2xl overflow-hidden shadow-xl">
          {/* Hero section with image */}
          <div className="relative h-64 sm:h-80 md:h-96">
            <img
              src={discount.image_url}
              alt={discount.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
              <div className="flex items-center space-x-4">
                {discount.discount_icon_url && (
                  <img
                    src={discount.discount_icon_url}
                    alt={`${discount.contact_name} logo`}
                    className="h-16 w-16 rounded-xl bg-white p-2 object-contain"
                  />
                )}
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                    {discount.name}
                  </h1>
                  <div className="flex items-center space-x-2">
                    {discount.offer && (
                      <span className="bg-primary text-white text-sm font-semibold px-3 py-1 rounded-full">
                        {discount.offer}
                      </span>
                    )}
                    {/* {discount.oo_recommended_text && (
                      <span className="bg-yellow-500/20 text-yellow-400 text-sm font-semibold px-3 py-1 rounded-full flex items-center">
                        <StarIcon className="h-4 w-4 mr-1" />
                        Recommended
                      </span>
                    )} */}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content section */}
          <div className="p-6 sm:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main content */}
              <div className="lg:col-span-2 space-y-8">
                {/* Recommended by Open Org callout */}
                {discount.oo_recommended_text && (
                  <div className="flex items-start bg-yellow-500/10 border-l-4 border-yellow-400 rounded-xl p-4 mb-8">
                    <div className="mr-3 mt-0.5 flex-shrink-0"><Logo minimal size="small" /></div>
                    <div>
                      <div className="font-semibold text-yellow-300 mb-1">Why we love {discount.name}</div>
                      <div className="text-yellow-100 text-sm leading-relaxed">{discount.oo_recommended_text}</div>
                    </div>
                  </div>
                )}

                {/* Description */}
                <div>
                  <h2 className="text-xl font-semibold text-white mb-4">About</h2>
                  <p className="text-gray-300 leading-relaxed whitespace-pre-line">{discount.description}</p>
                </div>

                {/* Tags */}
                <div>
                  <h2 className="text-xl font-semibold text-white mb-4">Categories</h2>
                  <div className="flex flex-wrap gap-2">
                    {discount.tags.map((tag) => (
                      <span
                        key={tag}
                        className="bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Company details */}
                <div>
                  <h2 className="text-xl font-semibold text-white mb-4">Company details</h2>
                  <div className="space-y-4">
                    {discount.name && (
                      <div className="flex items-center text-gray-300">
                        <BuildingOfficeIcon className="h-5 w-5 mr-3 text-primary-light" />
                        <span>{discount.name}</span>
                      </div>
                    )}
                    {discount.contact_name && (
                      <div className="flex items-center text-gray-300">
                        <UserGroupIcon className="h-4 w-4 text-primary-light mr-2" />
                        <span className="text-xs font-semibold bg-gray-800 text-gray-200 px-3 py-1 rounded-full border border-gray-700">
                          {discount.contact_name}
                        </span>
                      </div>
                    )}
                    {discount.locations && (
                      <div className="flex items-center text-gray-300 flex-wrap">
                        <MapPinIcon className="h-5 w-5 mr-3 text-primary-light" />
                        <span className="mr-2 font-semibold text-white">Locations served:</span>
                        {(Array.isArray(discount.locations)
                          ? discount.locations
                          : String(discount.locations).replace(/[\[\]{}"]+/g, '').split(',')
                        ).map((loc, idx) => {
                          const trimmed = loc.trim();
                          const flagMap = {
                            'UK': '🇬🇧',
                            'US': '🇺🇸',
                            'Europe': '🇪🇺',
                            'Australia': '🇦🇺',
                            'APAC': '🌏',
                            'Global': '🌍',
                            'MENA': '🌍',
                            'All': '🌍',
                          };
                          return (
                            <span
                              key={trimmed + idx}
                              className="bg-gray-700 text-gray-200 px-3 py-1 rounded-full text-xs font-medium mr-2 mb-1 inline-flex items-center"
                            >
                              {flagMap[trimmed] && <span className="mr-1">{flagMap[trimmed]}</span>}{trimmed}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {discount.headcount && (
                      <div className="flex items-center text-gray-300 flex-wrap">
                        <UserGroupIcon className="h-5 w-5 mr-3 text-primary-light" />
                        <span className="mr-2 font-semibold text-white">Company size served:</span>
                        {Array.isArray(discount.headcount)
                          ? discount.headcount.map((size, idx) => (
                              <span
                                key={size + idx}
                                className="bg-gray-700 text-gray-200 px-3 py-1 rounded-full text-xs font-medium mr-2 mb-1 inline-block"
                              >
                                {size}
                              </span>
                            ))
                          : (
                            <span className="bg-gray-700 text-gray-200 px-3 py-1 rounded-full text-xs font-medium inline-block">
                              {discount.headcount}
                            </span>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Discount code */}
                {discount.discount_code && (
                  <div className="bg-gray-700/50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Your discount code</h3>
                    <div className="flex items-center justify-between bg-gray-800 rounded-lg p-3">
                      <div className="flex items-center">
                        <TagIcon className="h-5 w-5 text-primary-light mr-2" />
                        <code className="text-white font-mono">{discount.discount_code}</code>
                      </div>
                      <button
                        onClick={handleCopyCode}
                        className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                      >
                        {copiedCode ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Contact buttons */}
                <div className="bg-gray-700/50 rounded-xl p-6 space-y-4">
                  <h3 className="text-lg font-semibold text-white mb-4">Get in touch</h3>
                  {discount.website_url && (
                    <a
                      href={addUTMParameters(discount.website_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-full bg-gray-800 hover:bg-gray-700 text-white px-4 py-3 rounded-lg transition-colors"
                    >
                      <GlobeAltIcon className="h-5 w-5 mr-2" />
                      Visit Website
                    </a>
                  )}
                  {discount.contact_email && (
                    <a
                      href={`mailto:${discount.contact_email}`}
                      className="flex items-center justify-center w-full bg-gray-800 hover:bg-gray-700 text-white px-4 py-3 rounded-lg transition-colors"
                    >
                      <EnvelopeIcon className="h-5 w-5 mr-2" />
                      Contact via Email
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscountDetail; 